import 'core-js/features/array/at';

import * as pdfjs from 'pdfjs-dist/build/pdf';
import * as pdfjsWorker from 'pdfjs-dist/build/pdf.worker';

import { CACHE_IR_VIRTUAL_ID, isBuild } from '@/const/host';
import i18n, { EN, ZH_HK } from '@/locales';

import { Base64_encode } from '@/utils/encrypt';
import { FILE_VIEWER_URL } from '@/utils/host';
import moment from 'moment';

const workerBlob = new Blob([pdfjsWorker], { type: 'text/javascript' });
const workerBlobURL = URL.createObjectURL(workerBlob);
pdfjs.GlobalWorkerOptions.workerSrc = workerBlobURL;

export const isArray = o => {
	return Object.prototype.toString.call(o) === '[object Array]';
};
export const isObject = o => {
	return Object.prototype.toString.call(o) === '[object Object]';
};
export const isFunction = o => {
	return (
		Object.prototype.toString.call(o) === '[object Function]' ||
		Object.prototype.toString.call(o) === '[object AsyncFunction]'
	);
};
export const isString = o => {
	return Object.prototype.toString.call(o) === '[object String]';
};
export const isNumber = o => {
	return Object.prototype.toString.call(o) === '[object Number]';
};
export const isBoolean = o => {
	return Object.prototype.toString.call(o) === '[object Boolean]';
};
export const isDate = o => {
	return Object.prototype.toString.call(o) === '[object Date]';
};
export const isNull = o => {
	return Object.prototype.toString.call(o) === '[object Null]';
};
export const isUndefined = o => {
	return Object.prototype.toString.call(o) === '[object Undefined]';
};

export const get100vh = () => {
	return `${window.innerHeight || document.documentElement.clientHeight}px`;
};

/**
 * 基于完整 URL 判断是否为 surf 模板
 * @returns {boolean}
 */
export const isSurfTemplate = () => {
	return /\/surf/.test(window.location.href);
};

// 空函数
export function noop() { }
/**
 * 深度克隆
 */
export const deepClone = obj => {
	if (typeof obj !== 'object' || obj === null) {
		return obj;
	}
	let res;
	if (obj instanceof Array) {
		res = [];
	} else {
		res = {};
	}
	for (let key in obj) {
		if (Object.prototype.hasOwnProperty.call(obj, key)) {
			res[key] = deepClone(obj[key]);
		}
	}
	return res;
};
/**
 * 验证区号
 *
 * @param  { String } code
 * @return { Boolean }
 */
export const checkAreaCode = code => /^\+?[0-9]+$/.test(code);

function getTargetParamFromURL(targetKey) {
	const urlParams = new URLSearchParams(window.location.search);
	return urlParams.get(targetKey);
}
export const getIrInputStoreId = () => {
	const staticStoreId = sessionStorage.getItem(CACHE_IR_VIRTUAL_ID);
	const storeIdInUrl = getTargetParamFromURL('storeId');
	return staticStoreId || storeIdInUrl || null;
};
export const isLinkMode = () => {
	const modeInUrl = getTargetParamFromURL('mode');
	return modeInUrl === 'link';
};

/** 获取随机数 */
export const getUuid = (radix, len = 8) => {
	var chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'.split('');
	let uuid = [];
	let i;
	radix = radix || chars.length;
	if (len) {
		for (i = 0; i < len; i++) uuid[i] = chars[0 | (Math.random() * radix)];
	} else {
		let r;
		uuid[8] = uuid[13] = uuid[18] = uuid[23] = '-';
		uuid[14] = '4';
		for (i = 0; i < 36; i++) {
			if (!uuid[i]) {
				r = 0 | (Math.random() * 16);
				uuid[i] = chars[i == 19 ? (r & 0x3) | 0x8 : r];
			}
		}
	}
	return uuid.join('');
};

export const debounce = (func, wait = 300, immediate = false) => {
	var timeout;
	return function () {
		var context = this,
			args = arguments;
		var later = function () {
			timeout = null;
			if (!immediate) func.apply(context, args);
		};
		var callNow = immediate && !timeout;
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
		if (callNow) func.apply(context, args);
	};
};

export const throttle = (func, delay = 300) => {
	let lastCall = 0;
	return function () {
		let now = Date.now();
		if (now - lastCall < delay) {
			return;
		}
		lastCall = now;
		return func.apply(this, arguments);
	};
};

/**
 * 将数字转换为带中文单位的字符串
 * @param {number} amount - 需要转换的数字
 * @param {Object} options - 选项
 * @param {string} options.unitType - 单位类型，可选值为 'cn' 或 'en' 或 'hk'
 * @return {string} - 转换后的字符串
 */
export const convertNumberToCNUnit = (amount, options = {}) => {
	const { unitType = null } = options;

	// 检查输入是否为数字
	if (typeof amount !== 'number') {
		return { value: '- -', unit: '' };
	}

	const positive = amount > 0 ? 1 : -1;
	amount = Math.abs(amount);

	// 定义单位和对应的值
	const units = [
		{ name: '万亿', value: 1e12 },
		{ name: '亿', value: 1e8 },
		{ name: '万', value: 1e4 }
	];

	const enUnits = [
		{ name: 'B', value: 1e9 },
		{ name: 'M', value: 1e6 },
		{ name: 'K', value: 1e3 }
	];

	const hkUnits = [
		{ name: '萬億', value: 1e12 },
		{ name: '億', value: 1e8 },
		{ name: '萬', value: 1e4 }
	];

	const unitMap = {
		'cn': units,
		'en': enUnits,
		'hk': hkUnits
	};
	let selectedUnits;
	if (unitType) {
		selectedUnits = unitMap[unitType] || unitMap['cn'];
	} else {
		selectedUnits = i18n.locale === EN ? enUnits : i18n.locale === ZH_HK ? hkUnits : units;
	}

	// 遍历单位，找到合适的单位进行转换
	for (let unit of selectedUnits) {
		if (amount >= unit.value) {
			return { value: ((positive * amount) / unit.value).toFixed(2), unit: unit.name };
		}
	}

	// 如果没有合适的单位，直接返回原值
	return { value: (positive * amount).toFixed(2), unit: '' };
};
export const convertNumberToStr = num => {
	const obj = convertNumberToCNUnit(num);
	return obj.value + obj.unit;
};

export const isNonValue = value => {
	// if (value == undefined || value === '' || Number.isNaN(value)) return true;
	if (value == undefined || value === '' || Number.isNaN(value) || Number(value) == 0) return true;
	return false;
};
export const formatDefault = value => {
	if (isNonValue(value)) {
		return '-';
	}
	return value;
};

// 预览服务白名单
export const PREVIEW_WHITE_LIST = [
	'file-view.pro.lan.comein.cn',
	'file-view.comein.cn',
	'resource.comein.cn',
	'comein-files.oss-cn-hangzhou.aliyuncs.com',
	'media.comein.cn',
	'testresource.comein.cn',
	'esignoss.esign.cn',
	'oss.esign.cn',
	'database.comein.cn',
	'bigdata-s3.wmcloud.com',
	'disc.szse.cn',
	'disclosure.szse.cn',
	'eid.csrc.gov.cn',
	'kcb.sse.com.cn',
	'reportdocs.static.szse.cn',
	'static.cninfo.com.cn',
	'static.sse.com.cn',
	'v2.neeq.com.cn',
	'www.bse.cn',
	'www.neeq.com.cn',
	'www.sse.com.cn'
];

/**
 * 在线预览文件
 * @param { String } url 文件地址
 */
export function previewFile(url, watermark = false) {
	let _url = url;
	if (isBuild) {
		// 域名切换
		_url = _url.replace('comein-files.oss-cn-hangzhou.aliyuncs.com', 'media.comein.cn');
	}

	const createUrl = new URL(_url);
	const fileHost = createUrl.hostname;
	if (!PREVIEW_WHITE_LIST.includes(fileHost) && !/comein\.cn/i.test(_url)) {
		window.open(_url);
		return;
	}

	window.open(transferPreviewFile(_url, watermark));
}
export function transferPreviewFile(url, watermark = false) {
	// 解析url，添加pdfDownloadDisable = false
	const urlObj = new URL(url);
	urlObj.searchParams.set('pdfDownloadDisable', 'false');
	const newUrl = urlObj.toString();
	let link = `${FILE_VIEWER_URL}${encodeURIComponent(
		Base64_encode(newUrl)
	)}&officePreviewSwitchDisabled=true&officePreviewType=pdf`;
	if (!watermark) {
		link += `&watermarkTxt=${encodeURIComponent('')}`;
	}
	return link;
}

// 删除对象属性值为空的属性
export const removeProperty = obj => {
	Object.keys(obj).forEach(item => {
		if (
			obj[item] === '' ||
			obj[item] === undefined ||
			obj[item] === 'undefined' ||
			obj[item] === null ||
			obj[item] === 'null'
		)
			delete obj[item];
		if (obj[item] instanceof Array && obj[item].length <= 0) delete obj[item];
	});
	return obj;
};
export const deepRemoveProperty = obj => {
	Object.keys(obj).forEach(item => {
		if (typeof obj[item] === 'object' && obj[item] !== null) {
			deepRemoveProperty(obj[item]);
		}
	});
	return removeProperty(obj);
};

export const generateRandomString = length => {
	let result = '';
	const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	const charactersLength = characters.length;
	for (let i = 0; i < length; i++) {
		result += characters.charAt(Math.floor(Math.random() * charactersLength));
	}
	return result;
};

export const getRelativeDateDesc = inputDate => {
	var today = moment();
	var tomorrow = moment().add(1, 'days');
	var yesterday = moment().subtract(1, 'days');

	if (moment(inputDate).isSame(today, 'day')) {
		return i18n.t('enterprise.datepicker.today');
	} else if (moment(inputDate).isSame(tomorrow, 'day')) {
		return i18n.t('enterprise.datepicker.tomorrow');
	} else if (moment(inputDate).isSame(yesterday, 'day')) {
		return i18n.t('enterprise.datepicker.yesterday');
	} else {
		let d = moment(inputDate);

		if (i18n.locale === EN) {
			d.locale('en');
			return d.format('dddd');
		}

		d.locale('zh-cn');
		if (i18n.locale === ZH_HK) {
			return d.format('dddd').replace('星期', '週');
		}
		return d.format('dddd').replace('星期', '周'); // 返回周几
	}
};

/** 对象路径匹配 */
export const miniSafeGet = (obj, path, defaultValue) => {
	const result = String.prototype.split
		.call(path, /[,[\].]+?/)
		.filter(Boolean)
		.reduce((res, key) => (res !== null && res !== undefined ? res[key] : res), obj);
	return result === undefined || result === '' || result === obj ? defaultValue : result;
};

export function hexToRgb(hex) {
	hex = hex.replace(/^#/, '');

	if (hex.length === 3) {
		hex = hex
			.split('')
			.map(x => x + x)
			.join('');
	}

	const r = parseInt(hex.slice(0, 2), 16);
	const g = parseInt(hex.slice(2, 4), 16);
	const b = parseInt(hex.slice(4, 6), 16);

	return {
		r,
		g,
		b
	};
}

/**
 * 阿拉伯数字转换为中文
 * @param {number }  数字
 * @returns {string}
 */
export function numberToChinese(number) {
	const chineseNumbers = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
	const chineseUnits = ['', '十', '百', '千', '万', '亿'];

	const numberStr = String(number);
	let result = '';

	for (let i = 0; i < numberStr.length; i++) {
		const digit = parseInt(numberStr[i]);
		const unitPos = numberStr.length - i - 1;
		const unit = chineseUnits[unitPos];

		// 处理 10 的情况
		if (digit === 1 && unitPos === 1 && numberStr.length === 2) {
			result += chineseUnits[1];
		} else {
			// 不显示零，仅在非零数字后加上对应的中文数字和单位
			if (digit !== 0) {
				result += chineseNumbers[digit] + unit;
			} else {
				// 如果当前数字是零，并且不是最后一个数字，则跳过零的显示
				const nextDigit = parseInt(numberStr[i + 1]);
				if (nextDigit !== 0) {
					result += chineseNumbers[digit];
				}
			}
		}
	}

	// 特殊情况处理，如果结果以零结尾，则去除末尾的零
	if (result.endsWith(chineseNumbers[0])) {
		result = result.slice(0, -1);
	}

	return result;
}

export async function getPDFCover(link) {
	try {
		const pdf = await pdfjs.getDocument({
			url: link,
			cMapUrl: 'https://image.comein.cn/web/irm/js/pdfjs-dist/cmaps/',
			cMapPacked: true,
		}).promise;
		const page = await pdf.getPage(1);

		const viewport = page.getViewport({ scale: 1 });
		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d');
		canvas.width = viewport.width;
		canvas.height = viewport.height;

		await page.render({ canvasContext: context, viewport: viewport }).promise;

		return canvas.toDataURL('image/png', 1);
	} catch {
		return '';
	}
}

/**
 * 获取时间戳范围
 * */
export const getTimeStamp = type => {
	let startTime, endTime;
	let date = new Date();

	switch (type) {
		case 'lastFive':
			// 过去5天
			startTime = new Date(date.getFullYear(), date.getMonth(), date.getDate() - 4).getTime();
			endTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59).getTime();
			return [startTime, endTime];
		case 'lastNinety':
			//过去90天
			startTime = new Date(date.getFullYear(), date.getMonth(), date.getDate() - 89).getTime();
			endTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59).getTime();
			return [startTime, endTime];
		case 'lastOneYear':
			// 过去1年
			startTime = moment().subtract(1, 'year').startOf('day').valueOf();
			endTime = moment().endOf('day').valueOf();
			return [startTime, endTime];
		case 'lastThreeYear':
			// 过去3年
			startTime = moment().subtract(3, 'year').startOf('day').valueOf();
			endTime = moment().endOf('day').valueOf();
			return [startTime, endTime];
		case 'lastFiveYear':
			// 过去5年
			startTime = moment().subtract(5, 'year').startOf('day').valueOf();
			endTime = moment().endOf('day').valueOf();
			return [startTime, endTime];
		default:
			return [];
	}
};

/**
 * 调整颜色值，加上一点白色，一般是 echarts 使用
 * @param {string} color 如 #eeff12
 * @param {number} amount 混合白色比例
 * @returns {string}
 */
export function transformColor(color, amount = 0.2) {
	if (!color) {
		return '';
	}
	// 将颜色转换为 RGB 值
	let r = parseInt(color.slice(1, 3), 16);
	let g = parseInt(color.slice(3, 5), 16);
	let b = parseInt(color.slice(5, 7), 16);

	// 计算加入白色后的 RGB 值
	r = Math.round((1 - amount) * r + amount * 255);
	g = Math.round((1 - amount) * g + amount * 255);
	b = Math.round((1 - amount) * b + amount * 255);

	// 将 RGB 值转换为十六进制颜色值
	color = '#' + r.toString(16).padStart(2, '0') + g.toString(16).padStart(2, '0') + b.toString(16).padStart(2, '0');
	return color;
}

export function splitFullCode(fullCode) {
	const regex = /^([a-zA-Z]*)(\d+)$/;
	const match = fullCode.match(regex);

	if (match) {
		return { market: match[1], code: match[2] };
	} else {
		return { market: '', code: '' }; // 如果没有匹配，返回空字符串
	}
}

export const appendParamsToUrl = (url, params) => {
	// 检查params是否为对象，如果不是则返回原始URL
	if (typeof params !== 'object' || params === null || Array.isArray(params)) {
		return url;
	}

	// 创建一个URLSearchParams实例
	const searchParams = new URLSearchParams();

	// 遍历params对象，添加键值对到searchParams中
	for (const key in params) {
		if (params[key] !== undefined && params[key] !== null) {
			searchParams.append(key, params[key]);
		}
	}

	// 获取查询字符串
	const queryString = searchParams.toString();

	// 如果查询字符串为空，则返回原始URL
	if (queryString === '') {
		return url;
	}

	// 如果URL已经包含查询字符串，使用'&'连接
	// 否则，使用'?'连接
	return url + (url.includes('?') ? '&' : '?') + queryString;
};
export const safeOpenUrl = (url, query = {}) => {
	try {
		// 尝试创建 URL 对象
		const createUrl = new URL(url);

		// https://irm.cninfo.com.cn/ircs/search?keyword=%E5%AE%8F%E5%88%9B%E6%8E%A7%E8%82%A1
		const errHost = ['irm.cninfo.com.cn'];
		const fileHost = createUrl.hostname;
		if (errHost.includes(fileHost)) {
			let link = document.createElement('a');
			link.href = url;
			link.rel = 'noreferrer';
			link.target = '_blank';
			document.body.appendChild(link);
			link.click();
			return;
		}

		const appendedUrl = appendParamsToUrl(url, query);
		window.open(appendedUrl, '_blank');
	} catch (e) {
		// 如果创建失败，假设默认使用 https，这能兼容大多数的链接配置场景
		const fullUrl = `https://${url}`;
		window.open(fullUrl, '_blank');
	}
};

export const parseQueryParams = url => {
	const queryString = url.split('?')[1];
	if (queryString) {
		const params = new URLSearchParams(queryString);
		const result = {};
		for (const [key, value] of params) {
			result[key] = value;
		}
		return result;
	}
	return {};
};

export const calcYoY = (val, yoyVal) => {
	if (val != undefined && yoyVal != undefined) {
		if (yoyVal === 0) {
			if (val === 0) {
				return 0;
			} else if (val > 0) {
				return 100;
			} else {
				return -100;
			}
		} else {
			return ((val - yoyVal) / Math.abs(yoyVal)) * 100;
		}
	} else {
		return null;
	}
};

export const getRect = el => {
	if (!el) return null;
	if (el instanceof window.SVGElement || 'getBoundingClientRect' in el) {
		let rect = el.getBoundingClientRect();
		return {
			top: rect.top,
			left: rect.left,
			right: rect.right,
			bottom: rect.bottom,
			width: rect.width,
			height: rect.height
		};
	} else {
		return {
			top: el.offsetTop,
			left: el.offsetLeft,
			width: el.offsetWidth,
			height: el.offsetHeight
		};
	}
};

export const getScheduleTypeLabelByCode = value => {
	const activityMap = {
		1: i18n.t('enterprise.IR-events'),
		2: i18n.t('enterprise.announcement.title'),
		3: i18n.t('enterprise.news'),
		4: i18n.t('enterprise.earnings-disclosure'),
		5: i18n.t('enterprise.shareholders-meeting')
	};
	return activityMap[value];
};

/**
 * 创建模块映射表
 * @param {Object} context - require.context 的结果
 * @param {RegExp} regex - 用于匹配模块名的正则表达式
 * @param {string[]} targetIds - 特定匹配的模块名
 * @returns {Object}
 */
export const createModuleMap = (context, regex, targetIds = []) => {
	const moduleMap = {};
	context.keys().forEach(filePath => {
		const matchResult = filePath.match(regex);
		if (matchResult) {
			const moduleName = matchResult[1];
			if (!targetIds.length || targetIds.includes(moduleName)) {
				moduleMap[moduleName] = context(filePath).default;
			}
		}
	});
	return moduleMap;
};

/**
 * 基于 MutationObserver 监听元素高度变化
 * @param {HTMLElement} el        目标元素
 * @param {Function}   callback  回调函数，接收当前高度（Number）
 * @param {Object}     [options] MutationObserver 配置，默认监听属性、子节点及子树
 * @returns {Function}           stop() 停止监听
 */
export const observeHeightMO = (
	el,
	callback,
	options = {
		attributes: true,
		childList: true,
		subtree: true
	}
) => {
	let lastHeight = el.scrollHeight;
	const mo = new MutationObserver(() => {
		const currentHeight = el.scrollHeight;
		if (currentHeight !== lastHeight) {
			lastHeight = currentHeight;
			callback(currentHeight);
		}
	});
	mo.observe(el, options);
	return function stop() {
		mo.disconnect();
	};
};

export const checkErrorValue = value => {
	const targetSet = new Set([undefined, 'undefined', null, 'null']);
	return targetSet.has(value);
};
export const isErrorValue = (key, value) => {
	if (checkErrorValue(value)) {
		console.warn(`No Value [${key}]: ${value}`);
		return true;
	}
	return false;
};
/**
 * @description 不四舍五入，直接截断
 * @param {*} num
 * @param {*} decimals
 * @returns
 */
export const toFixedNoRound = (num, decimals = 2) => {
	if (num === null || num === undefined) {
		return num;
	}
	// 将数字转换为字符串并分成整数和小数部分
	const numStr = num.toString();
	const parts = numStr.split('.');
	let result = num;

	// 如果没有小数部分，直接返回
	if (parts.length === 1) {
		result = num.toFixed(decimals);
	}

	// 处理负数
	let isNegative = false;
	if (parts[0].startsWith('-')) {
		isNegative = true;
		parts[0] = parts[0].substring(1);
	}

	// 截取所需的小数位数
	const integerPart = parts[0];
	let decimalPart = parts[1];

	if (decimals > 0) {
		// 如果指定的小数位数比实际多，补零
		if (decimalPart.length < decimals) {
			decimalPart = decimalPart.padEnd(decimals, '0');
		} else {
			// 直接截断，不四舍五入
			decimalPart = decimalPart.substring(0, decimals);
		}
		result = (isNegative ? '-' : '') + integerPart + '.' + decimalPart;
	} else {
		// 如果小数位数为0，直接返回整数部分
		result = (isNegative ? '-' : '') + integerPart;
	}
	return +result;
};
